import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, Clapperboard } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Wraps every protected page. When the user's trial has lapsed and they
 * have no active subscription, swap the page out for a paywall overlay —
 * except on the /billing* routes, which must stay reachable so the user
 * can actually pay.
 */
export default function PaywallGuard({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const billing = user?.billing;
  const isLocked = billing && billing.is_active === false;
  const onBillingRoute = location.pathname.startsWith("/billing");

  if (!isLocked || onBillingRoute) return <>{children}</>;

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="paywall-overlay">
      <div className="w-full max-w-lg bg-[#14181F] border border-amber-500/40 rounded-2xl p-8 text-center shadow-xl">
        <div className="flex items-center justify-center gap-2 mb-6 text-gray-500">
          <Clapperboard size={18} className="text-amber-400" />
          <span className="font-[Outfit] font-bold text-white">Get2Set</span>
        </div>
        <Lock size={44} className="mx-auto text-amber-400" />
        <h1 className="mt-4 text-2xl font-bold">Your free trial has ended</h1>
        <p className="text-sm text-gray-400 mt-3">
          Pick a plan to keep generating call sheets, distributing them to your crew, and tracking RSVPs.
          Monthly or yearly — cancel anytime.
        </p>
        <button
          data-testid="paywall-upgrade-btn"
          onClick={() => navigate("/billing")}
          className="mt-6 w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-3 rounded-lg"
        >
          See plans
        </button>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          className="mt-3 text-xs text-gray-500 hover:text-gray-300"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
